<template>
  <v-navigation-drawer
    v-model="internalValue"
    fixed
    right
    temporary
    hide-overlay
    width="950"
    style="z-index: 9999;"
    class="elevation-12 border-left bg-white drawer-customer-lines"
  >
    <div class="d-flex flex-column" style="height: 100vh;">
      <!-- DRAWER HEADER -->
      <div class="py-4 px-5 border-light-bottom bg-white d-flex align-center justify-space-between flex-wrap">
        <div class="d-flex align-center">
          <v-avatar color="grey lighten-4" size="42" class="mr-3">
            <v-icon color="primary">mdi-account-details-outline</v-icon>
          </v-avatar>
          <div>
            <div class="d-flex align-center flex-wrap">
              <span class="text-h6 font-weight-black grey--text text--darken-4 mr-2">
                {{ customer ? (customer.IsNoClient ? 'Sin Cliente Asignado' : customer.CustomerName) : 'Detalle de Líneas' }}
              </span>
              <v-chip
                v-if="customer && customer.PrsCustomerID"
                x-small
                label
                outlined
                color="primary"
                class="font-weight-bold"
              >
                SAP: {{ customer.PrsCustomerID }}
              </v-chip>
            </div>
            <div class="caption grey--text text--darken-2">
              Líneas programadas a través de todas las programaciones comerciales activas.
            </div>
          </div>
        </div>

        <v-btn icon color="grey darken-2" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- SUMMARY KPI CHIPS STRIP -->
      <div class="px-5 py-3 bg-grey-lighten-5 border-light-bottom">
        <v-row dense align="center">
          <v-col cols="6" sm="3" class="text-center">
            <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Líneas</span>
            <div class="text-subtitle-2 font-weight-black grey--text text--darken-4">
              {{ filteredLines.length }}
            </div>
          </v-col>
          <v-divider vertical class="my-1 hidden-xs-only"></v-divider>

          <v-col cols="6" sm="3" class="text-center">
            <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total TM</span>
            <div class="text-subtitle-2 font-weight-black green--text text--darken-3">
              {{ summaryTons | formatNumber }} TM
            </div>
          </v-col>
          <v-divider vertical class="my-1 hidden-xs-only"></v-divider>

          <v-col cols="6" sm="3" class="text-center">
            <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total FCL</span>
            <div class="text-subtitle-2 font-weight-black teal--text text--darken-3">
              {{ summaryFcl | formatNumber }} FCL
            </div>
          </v-col>
          <v-divider vertical class="my-1 hidden-xs-only"></v-divider>

          <v-col cols="6" sm="3" class="text-center">
            <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total FOB USD</span>
            <div class="text-subtitle-2 font-weight-black blue--text text--darken-3">
              ${{ summaryFobUsd | formatNumber }}
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- FILTERS ROW -->
      <div class="px-5 py-3 border-light-bottom bg-white">
        <v-row dense align="center">
          <v-col cols="12" sm="5">
            <s-text
              v-model="search"
              placeholder="Buscar por programación, producto, SAP, marca, contrato..."
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="4">
            <s-select
              v-model="filterCommercialStatus"
              :items="statusItems"
              label="Estado de Línea"
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" sm="3" class="text-right">
            <v-btn
              text
              small
              black
              class="font-weight-bold text-none rounded-lg"
              @click="loadCustomerLines"
              :loading="loading"
            >
              <v-icon style="font-size:16px;" class="mr-1">mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- DRAWER CONTENT: LINES DATA TABLE -->
      <div class="flex-grow-1 overflow-y-auto px-5 py-3">
        <v-skeleton-loader v-if="loading" type="table" />

        <div v-else-if="filteredLines.length === 0" class="text-center py-10">
          <v-icon size="56" color="grey lighten-1">mdi-clipboard-text-off-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold grey--text text--darken-2 mt-2">
            No se encontraron líneas comerciales
          </div>
          <div class="caption grey--text text--darken-1">
            No hay registros que coincidan con los filtros seleccionados.
          </div>
        </div>

        <v-data-table
          v-else
          :headers="headers"
          :items="filteredLines"
          dense
          hide-default-footer
          :items-per-page="-1"
          class="border-rounded border-light elevation-0 lines-table"
        >
          <!-- Programación & Fecha -->
          <template v-slot:[`item.EcpName`]="{ item }">
            <div>
              <span class="font-weight-bold grey--text text--darken-4">{{ item.EcpName }}</span>
              <div class="caption grey--text text--darken-2">
                <v-icon x-small color="grey darken-2">mdi-calendar-clock</v-icon>
                {{ formatDate(item.EcpDate) }}
              </div>
            </div>
          </template>

          <!-- Producto & SAP -->
          <template v-slot:[`item.EpdProduct`]="{ item }">
            <div>
              <span class="font-weight-bold grey--text text--darken-4">{{ item.EpdProduct }}</span>
              <div v-if="item.EpdSapCode" class="caption grey--text text--darken-2">
                <code>{{ item.EpdSapCode }}</code>
              </div>
            </div>
          </template>

          <!-- Marca / Empaque -->
          <template v-slot:[`item.BrandPackaging`]="{ item }">
            <div>
              <span class="body-2 font-weight-bold grey--text text--darken-4">{{ item.EpdBrand || '-' }}</span>
              <div class="caption grey--text text--darken-2">
                {{ item.EpdPackaging || '-' }}
              </div>
            </div>
          </template>

          <!-- Capacidad -->
          <template v-slot:[`item.CapacityInfo`]="{ item }">
            <div>
              <span class="font-weight-bold body-2 grey--text text--darken-4">
                {{ item.EpdCapacity ? Number(item.EpdCapacity).toFixed(2) + ' TM/FCL' : '-' }}
              </span>
              <div v-if="item.EpdIsCapacityException" class="caption">
                <v-chip x-small color="amber darken-2" text-color="white" class="font-weight-bold">
                  Excepción
                </v-chip>
              </div>
            </div>
          </template>

          <!-- TM Programadas -->
          <template v-slot:[`item.EpdProgrammedTons`]="{ item }">
            <span class="font-weight-black green--text text--darken-3">
              {{ Number(item.EpdProgrammedTons || 0).toFixed(2) }}
            </span>
          </template>

          <!-- FCL Programados -->
          <template v-slot:[`item.EpdProgrammedFcl`]="{ item }">
            <span class="font-weight-black teal--text text--darken-3">
              {{ Number(item.EpdProgrammedFcl || 0).toFixed(2) }}
            </span>
          </template>

          <!-- Incoterm -->
          <template v-slot:[`item.IncotermName`]="{ item }">
            <v-chip x-small label outlined color="primary" class="font-weight-bold">
              {{ item.IncotermName || 'FOB' }}
            </v-chip>
          </template>

          <!-- Precio FOB Equiv -->
          <template v-slot:[`item.EpdPriceFobEquivalent`]="{ item }">
            <span class="font-weight-bold body-2 grey--text text--darken-4">
              ${{ Number(item.EpdPriceFobEquivalent || 0).toFixed(4) }}
            </span>
          </template>

          <!-- Importe FOB Equiv -->
          <template v-slot:[`item.EpdAmountFobEquivalent`]="{ item }">
            <span class="font-weight-black blue--text text--darken-3">
              ${{ Number(item.EpdAmountFobEquivalent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            </span>
          </template>

          <!-- Estado Comercial -->
          <template v-slot:[`item.EpdCommercialStatus`]="{ item }">
            <v-chip
              x-small
              class="font-weight-bold"
              :color="item.EpdCommercialStatus === 2 ? 'green lighten-5' : 'blue lighten-5'"
              :text-color="item.EpdCommercialStatus === 2 ? 'green darken-4' : 'blue darken-4'"
            >
              <v-icon x-small class="mr-1">
                {{ item.EpdCommercialStatus === 2 ? 'mdi-file-certificate-outline' : 'mdi-clock-outline' }}
              </v-icon>
              {{ item.CommercialStatusName || (item.EpdCommercialStatus === 2 ? 'Vendido' : 'Planificado') }}
            </v-chip>
          </template>

          <!-- Contrato -->
          <template v-slot:[`item.EpdContractNumber`]="{ item }">
            <span v-if="item.EpdContractNumber" class="font-weight-bold text-caption grey--text text--darken-4">
              <v-icon x-small color="grey darken-3">mdi-pound</v-icon>
              {{ item.EpdContractNumber }}
            </span>
            <span v-else class="text-caption grey--text text--darken-1">-</span>
          </template>

          <!-- Auditoría -->
          <template v-slot:[`item.AuditInfo`]="{ item }">
            <div class="caption grey--text text--darken-2">
              <div>
                <v-icon x-small color="grey darken-2">mdi-account-outline</v-icon>
                {{ item.LastModifyUser || item.CreateUserName || 'Sistema' }}
              </div>
              <div>
                <v-icon x-small color="grey darken-2">mdi-clock-time-four-outline</v-icon>
                {{ formatDateTime(item.LastModifyDate || item.SecCreate) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- DRAWER FOOTER -->
      <div class="py-3 px-5 border-light-top bg-white d-flex align-center justify-space-between">
        <span class="caption grey--text text--darken-2">
          Mostrando {{ filteredLines.length }} registros
        </span>
        <v-btn text class="rounded-lg px-4 text-none font-weight-bold" @click="close">
          Cerrar
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import CommercialMasterBudgetService from "@/services/ExternalTrade/CommercialMasterBudgetService";

export default {
  name: "CustomerProgrammingDrawer",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    embId: {
      type: Number,
      default: 0,
    },
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      lines: [],
      loading: false,
      search: "",
      filterCommercialStatus: null,
      statusItems: [
        { text: "Todos los Estados", value: null },
        { text: "Planificado (Open Volume)", value: 1 },
        { text: "Vendido con Contrato", value: 2 },
      ],
      headers: [
        { text: "Programación / Fecha", value: "EcpName", align: "left" },
        { text: "Producto / SAP", value: "EpdProduct", align: "left" },
        { text: "Marca / Empaque", value: "BrandPackaging", align: "left" },
        { text: "Capacidad", value: "CapacityInfo", align: "center" },
        { text: "TM", value: "EpdProgrammedTons", align: "right" },
        { text: "FCL", value: "EpdProgrammedFcl", align: "right" },
        { text: "Incoterm", value: "IncotermName", align: "center" },
        { text: "FOB Equiv ($/kg)", value: "EpdPriceFobEquivalent", align: "right" },
        { text: "FOB Total USD", value: "EpdAmountFobEquivalent", align: "right" },
        { text: "Estado", value: "EpdCommercialStatus", align: "center" },
        { text: "Contrato", value: "EpdContractNumber", align: "center" },
        { text: "Última Modificación", value: "AuditInfo", align: "left" },
      ],
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    filteredLines() {
      let list = this.lines || [];

      if (this.filterCommercialStatus !== null && this.filterCommercialStatus !== undefined) {
        list = list.filter((l) => l.EpdCommercialStatus === this.filterCommercialStatus);
      }

      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        list = list.filter(
          (l) =>
            (l.EcpName && l.EcpName.toLowerCase().includes(q)) ||
            (l.EpdProduct && l.EpdProduct.toLowerCase().includes(q)) ||
            (l.EpdSapCode && l.EpdSapCode.toLowerCase().includes(q)) ||
            (l.EpdBrand && l.EpdBrand.toLowerCase().includes(q)) ||
            (l.EpdPackaging && l.EpdPackaging.toLowerCase().includes(q)) ||
            (l.EpdContractNumber && l.EpdContractNumber.toLowerCase().includes(q)) ||
            (l.IncotermName && l.IncotermName.toLowerCase().includes(q))
        );
      }

      return list;
    },
    summaryTons() {
      return this.filteredLines.reduce((acc, curr) => acc + Number(curr.EpdProgrammedTons || 0), 0);
    },
    summaryFcl() {
      return this.filteredLines.reduce((acc, curr) => acc + Number(curr.EpdProgrammedFcl || 0), 0);
    },
    summaryFobUsd() {
      return this.filteredLines.reduce((acc, curr) => acc + Number(curr.EpdAmountFobEquivalent || 0), 0);
    },
  },
  watch: {
    value(val) {
      if (val && this.embId && this.customer) {
        this.loadCustomerLines();
      }
    },
    customer() {
      if (this.value && this.embId && this.customer) {
        this.loadCustomerLines();
      }
    },
  },
  methods: {
    loadCustomerLines() {
      if (!this.embId || !this.customer) return;

      this.loading = true;
      const payload = {
        EmbID: this.embId,
        PrsCustomerID: this.customer.IsNoClient ? null : this.customer.PrsCustomerID,
        IsNoClient: this.customer.IsNoClient ? 1 : 0,
        EpdCommercialStatus: null,
      };

      CommercialMasterBudgetService.getCustomerProgrammingLines(payload, this.$fun.getUserID())
        .then((res) => {
          this.lines = res.data || [];
        })
        .catch((err) => {
          this.$fun.alert(
            "Error al obtener las líneas del cliente: " +
              (err.response?.data?.Message || err.message),
            "error"
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.internalValue = false;
    },
    formatDate(d) {
      if (!d) return "-";
      return d.substr(0, 10);
    },
    formatDateTime(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString();
    },
  },
  filters: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.drawer-customer-lines {
  z-index: 9999 !important;
}
.lines-table ::v-deep th {
  background-color: #f8fafc !important;
  color: #1e293b !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0 !important;
}
.lines-table ::v-deep td {
  font-size: 0.825rem !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
